import { SearchBrowseParams } from '@aibees/elephant/types';
import { Icon, UserFullName } from '@aibees/shared/components';
import { ParsedPreset } from '@aibees/shared/definition';

import {
  DATE_FORMAT_DD_MMM_YYYY,
  fDateShort,
  fNumber,
} from '@aibees/shared/utils';

import { AdvanceTableConfig } from '@aibees/shared/features';

import { BrowseSearchExportsRequest, ExportItem } from '@aibees/elephant/hooks';
import { Label } from '@aibees/elephant/types/label';
import { ReactNode } from 'react';
import { EnrichTable } from './components/enrich-table';
import { ExportTable } from './components/export-table';
import { LabelTable } from './components/label-table';
import { ListTable } from './components/list-table';
import { SavedCardTable } from './components/saved-card-table';
import {
  CardBodyDate,
  CardBodyTeam,
  CardBodyTitle,
  CardBodyTotalRecords,
} from './styles';
import { HeaderMenuTitle, searchListHeaderConfig } from './types';

export const searchListHeader: searchListHeaderConfig = {
  header: null,
  icon: <Icon name="bookmark" size={14} />,
  action: {
    title: 'View All',
    path: null,
  },
};

export const SavedConfig: Omit<
  AdvanceTableConfig<ParsedPreset<SearchBrowseParams>>,
  'browseApi'
> = {
  columns: [
    {
      id: 'name',
      label: 'Save Name',
      width: 104,
      render: (v) => <CardBodyTitle sx={{ width: 83 }}>{v}</CardBodyTitle>,
    },
    {
      id: 'user',
      label: 'Team',
      width: 85,
      render: (_, v) => (
        <CardBodyTeam>
          <UserFullName user={v.workspace.user} />
        </CardBodyTeam>
      ),
    },

    {
      id: 'modified',
      label: 'Created',
      width: 85,
      render: (v) => (
        <CardBodyDate>{fDateShort(v, DATE_FORMAT_DD_MMM_YYYY)}</CardBodyDate>
      ),
    },
  ],
  actions: [],
  filters: [],
  actionsConfig: {
    label: 'Action',
    minWidth: '10%',
    align: 'center',
    sx: { display: 'flex', justifyContent: 'center' },
  },
  hidePagination: true,
  stickyHeader: true,
  defaultOrderBy: 'modified',
  hideColumnsOnNoData: true,
  emptyProps: {
    emptyIcon: 'bookmark',
  },

  hideResetFilters: true,
  filtersSx: { px: 0, pt: 1, itemsSx: { flexDirection: 'column', gap: 2 } },
  cellSx: { p: (p) => p.spacing(1) },
  bordered: 'dashed',
  maxHeight: 214,
};

export const LabelsConfig: Omit<AdvanceTableConfig<Label>, 'browseApi'> = {
  columns: [
    {
      id: 'name',
      label: 'Label Name',
      width: 104,
      render: (v) => <CardBodyTitle sx={{ width: 83 }}>{v}</CardBodyTitle>,
    },
    {
      id: 'workspace',
      label: 'Team',
      width: 85,
      render: (_, v) => (
        <CardBodyTeam>
          <UserFullName user={v.workspace.user} />
        </CardBodyTeam>
      ),
    },

    {
      id: 'records',
      label: 'Total Records',
      width: 85,
      render: (v) => <CardBodyTotalRecords>{fNumber(v)}</CardBodyTotalRecords>,
    },
  ],
  actions: [],
  filters: [],
  hidePagination: true,
  stickyHeader: true,
  maxHeight: 235,
};

export const ListConfig: Omit<AdvanceTableConfig<Label>, 'browseApi'> = {
  columns: [
    {
      id: 'name',
      label: 'List Name',
      width: 104,
      render: (v) => <CardBodyTitle sx={{ width: 83 }}>{v}</CardBodyTitle>,
    },
    {
      id: 'workspace',
      label: 'Team',
      width: 85,
      render: (_, v) => (
        <CardBodyTeam>
          <UserFullName user={v.workspace.user} />
        </CardBodyTeam>
      ),
    },

    {
      id: 'records',
      label: 'Total Records',
      width: 85,
      render: (v) => <CardBodyTotalRecords>{fNumber(v)}</CardBodyTotalRecords>,
    },
  ],
  actions: [],
  filters: [],
  hidePagination: true,
  stickyHeader: true,
  maxHeight: 235,
};

export const exportConfig: Omit<
  AdvanceTableConfig<ExportItem, BrowseSearchExportsRequest>,
  'browseApi'
> = {
  columns: [
    {
      id: 'name',
      label: 'File Name',
      width: 104,
      render: (v) => <CardBodyTitle sx={{ width: 83 }}>{v}</CardBodyTitle>,
    },
    {
      id: 'workspace',
      label: 'Team',
      width: 85,
      render: (_, v) => (
        <CardBodyTeam>
          <UserFullName user={v.workspace.user} />
        </CardBodyTeam>
      ),
    },

    {
      id: 'records',
      label: 'Total Records',
      width: 85,
      render: (v) => <CardBodyTotalRecords>{fNumber(v)}</CardBodyTotalRecords>,
    },
    {
      id: 'status',
      label: 'Status',
      width: 85,
      render: (v) => v,
    },
  ],
  actions: [],
  filters: [],
  hidePagination: true,
  stickyHeader: true,
  maxHeight: 235,
};

export const table: {
  [key in Partial<HeaderMenuTitle>]: ReactNode;
} = {
  Saved: <SavedCardTable />,
  Labels: <LabelTable />,
  Lists: <ListTable />,
  Export: <ExportTable />,
  Enrich: <EnrichTable />,
};
